import { zodResolver } from "@hookform/resolvers/zod";
import { Email, ErrorOutline, Person } from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { z } from "zod";
import i18n from "../../../config/i18n";

import { countries, type Country } from "../../../utils/countries";
import {
  getNationalLengthForCountry,
  requiresStrictNationalLength,
  sanitizePhoneDigits,
  toE164,
  trimPhoneToNationalLength,
} from "../../../utils/CountryPhoneUtils";
import { alphaOnly } from "../../../utils/InputValidation";
import {
  GoogleRegistrationFormData, // keep this only for the onSubmit prop payload
} from "../../../validation/registerSchema";
import CountryCodeSelect from "../../common/CountryCodeSelect";

/** Custom helper text component with error icon */
const ErrorHelperText: React.FC<{ message?: string }> = ({ message }) => {
  if (!message) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        mt: 0.5,
        color: "error.main",
      }}
    >
      <ErrorOutline sx={{ fontSize: 16 }} />
      <Typography variant="caption" sx={{ color: "error.main" }}>
        {message}
      </Typography>
    </Box>
  );
};

/**
 * Local schema for THIS form only:
 * - boolean acceptTerms refined to true (so default false is allowed by TS)
 * - includes countryCode
 * - dynamic phone min length per country
 */
const getFormSchema = () => {
  const t = (key: string) => i18n.t(key);
  return z
    .object({
      email: z.string().email(t("validation.emailInvalid")),
      firstName: z.string().min(1, t("validation.firstNameRequired")),
      lastName: z.string().min(1, t("validation.lastNameRequired")),
      phoneNumber: z.string().min(1, t("validation.phoneRequired")),
      acceptTerms: z
        .boolean()
        .refine((v) => v === true, { message: t("validation.acceptTerms") }),
      countryCode: z.string().min(2).max(3), // e.g. US, TJ
      referralCode: z.string().max(20).optional().or(z.literal("")),
    })
    .superRefine((val, ctx) => {
      if (!requiresStrictNationalLength(val.countryCode)) {
        return;
      }

      const digits = sanitizePhoneDigits(val.phoneNumber);
      if (!digits) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("validation.phoneRequired"),
          path: ["phoneNumber"],
        });
        return;
      }

      const expectedLength = getNationalLengthForCountry(val.countryCode);
      if (digits.length !== expectedLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("validation.phoneInvalid"),
          path: ["phoneNumber"],
        });
      }
    });
};

type FormData = z.infer<ReturnType<typeof getFormSchema>>;

interface GoogleRegistrationFormProps {
  /** Google user data to pre-fill the form */
  googleUser: {
    email: string;
    firstName: string;
    lastName: string;
    profilePicture?: string;
  };
  /** Form submission handler */
  onSubmit: (data: GoogleRegistrationFormData) => void | Promise<void>;
  /** Loading state */
  isLoading?: boolean;
  /** Error message to display */
  error?: string | null;
  /** Cancel handler to go back */
  onCancel?: () => void;
}

const GoogleRegistrationForm: React.FC<GoogleRegistrationFormProps> = ({
  googleUser,
  onSubmit,
  isLoading = false,
  error = null,
  onCancel,
}) => {
  const { t: myTranslate } = useTranslation();
  const [searchParams] = useSearchParams();
  const referralCodeFromUrl = searchParams.get("ref") || "";
  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid, touchedFields, isSubmitted },
  } = useForm<FormData>({
    resolver: zodResolver(getFormSchema()),
    defaultValues: {
      email: googleUser.email,
      firstName: googleUser.firstName || "",
      lastName: googleUser.lastName || "",
      phoneNumber: "",
      acceptTerms: false, // ✅ allowed now (boolean, refined to true)
      countryCode: "TJ",
      referralCode: referralCodeFromUrl,
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const [selectedCountry, setSelectedCountry] = React.useState<
    Country | undefined
  >(countries.find((c) => c.code === "TJ") || undefined);

  const phoneValue = watch("phoneNumber");
  const countryCode = watch("countryCode");
  const maxLen = getNationalLengthForCountry(countryCode);

  // Only show phone error after user has finished typing (blur) or after form submission
  const phoneErrorVisible =
    !!errors.phoneNumber && (touchedFields.phoneNumber || isSubmitted);

  const handleCountryChange = (_: unknown, newCountry: Country | null) => {
    const next = newCountry ?? undefined;
    setSelectedCountry(next);

    const code = next?.code ?? "US";
    // Do NOT validate immediately to avoid instant red error when switching to TJ
    setValue("countryCode", code, { shouldDirty: true, shouldValidate: false });

    setValue("phoneNumber", trimPhoneToNationalLength(code, phoneValue || ""), {
      shouldDirty: true,
      shouldValidate: false,
    });
  };

  const submit = async (data: FormData) => {
    if (requiresStrictNationalLength(data.countryCode)) {
      const digits = sanitizePhoneDigits(data.phoneNumber);
      if (!digits) {
        setValue("phoneNumber", "", { shouldValidate: true });
        return;
      }

      const expectedLength = getNationalLengthForCountry(data.countryCode);
      if (digits.length !== expectedLength) {
        setValue("phoneNumber", data.phoneNumber, { shouldValidate: true });
        return;
      }
    }

    const e164 = toE164(data.countryCode, data.phoneNumber);
    const payload: GoogleRegistrationFormData = {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      phoneNumber: e164,
      acceptTerms: data.acceptTerms,
      referralCode: data.referralCode || undefined,
    };
    await onSubmit(payload);
  };

  return (
    <Paper elevation={2} sx={{ p: 3, maxWidth: 450, mx: "auto" }}>
      <Box sx={{ textAlign: "center", mb: 3 }}>
        {googleUser.profilePicture && (
          <Avatar
            src={googleUser.profilePicture}
            sx={{ width: 64, height: 64, mx: "auto", mb: 2 }}
          />
        )}
        <Typography
          variant="h6"
          className="notranslate"
          translate="no"
          gutterBottom
          fontWeight="bold"
        >
          {myTranslate("auth.completeYourRegistration")}
        </Typography>
        <Typography
          variant="body2"
          className="notranslate"
          translate="no"
          color="text.secondary"
        >
          {myTranslate("auth.almostDoneProvidePhone")}
        </Typography>
      </Box>

      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(submit)();
        }}
        sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
        noValidate
      >
        <Box>
          <TextField
            label={myTranslate("auth.emailAddress")}
            className="notranslate"
            translate="no"
            {...register("email")}
            fullWidth
            disabled
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email color="action" />
                </InputAdornment>
              ),
            }}
            error={!!errors.email}
          />
          <ErrorHelperText message={errors.email?.message} />
        </Box>

        <Box>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  label={myTranslate("auth.firstName")}
                  className="notranslate"
                  translate="no"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="action" />
                      </InputAdornment>
                    ),
                  }}
                  error={!!errors.firstName}
                  onChange={(e) =>
                    field.onChange(alphaOnly(e.target.value || ""))
                  }
                />
                <ErrorHelperText message={errors.firstName?.message} />
              </>
            )}
          />
        </Box>

        <Box>
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  label={myTranslate("auth.lastName")}
                  className="notranslate"
                  translate="no"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="action" />
                      </InputAdornment>
                    ),
                  }}
                  error={!!errors.lastName}
                  onChange={(e) =>
                    field.onChange(alphaOnly(e.target.value || ""))
                  }
                />
                <ErrorHelperText message={errors.lastName?.message} />
              </>
            )}
          />
        </Box>

        {/* keep countryCode registered in RHF */}
        <input type="hidden" {...register("countryCode")} />

        {/* PHONE uses Controller: digits-only; country dial as adornment */}
        <Controller
          name="phoneNumber"
          control={control}
          render={({ field }) => (
            <Box>
              <TextField
                {...field}
                label={myTranslate("auth.phoneNumber")}
                className="notranslate"
                translate="no"
                fullWidth
                required
                placeholder="(555) 123-4567"
                error={phoneErrorVisible}
                onChange={(e) => {
                  const trimmed = trimPhoneToNationalLength(
                    countryCode,
                    e.target.value || ""
                  );
                  field.onChange(trimmed);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 0.5 }}>
                      <CountryCodeSelect
                        value={selectedCountry?.code ?? "TJ"}
                        onChange={(country) =>
                          handleCountryChange(null, country)
                        }
                      />
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: maxLen,
                }}
              />
              <ErrorHelperText
                message={
                  phoneErrorVisible ? errors.phoneNumber?.message : undefined
                }
              />
              {!phoneErrorVisible && (
                <Typography
                  variant="caption"
                  className="notranslate"
                  translate="no"
                  sx={{ color: "text.secondary", mt: 0.5 }}
                >
                  {myTranslate("auth.phoneVerificationNote")}
                </Typography>
              )}
            </Box>
          )}
        />

        <input type="hidden" {...register("referralCode")} />

        <FormControlLabel
          control={<Checkbox {...register("acceptTerms")} color="primary" />}
          label={
            <Typography variant="body2" className="notranslate" translate="no">
              {myTranslate("auth.iAgreeToThe")}{" "}
              <Link
                component={RouterLink}
                to="/terms"
                target="_blank"
                color="inherit"
                underline="always"
              >
                {myTranslate("auth.termsOfService")}
              </Link>{" "}
              {myTranslate("auth.and")}{" "}
              <Link
                component={RouterLink}
                to="/privacyPolicy"
                target="_blank"
                color="inherit"
                underline="always"
              >
                {myTranslate("auth.privacyPolicy")}
              </Link>
            </Typography>
          }
        />
        {errors.acceptTerms && (
          <Typography
            variant="caption"
            color="error"
            className="notranslate"
            translate="no"
            sx={{ ml: 2 }}
          >
            {myTranslate("auth.pleaseAcceptTerms")}
          </Typography>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          className="notranslate"
          translate="no"
          disabled={isLoading || !isValid}
          sx={{ mt: 1, py: 1.5, fontWeight: 600 }}
        >
          {isLoading
            ? myTranslate("auth.creatingAccount")
            : myTranslate("auth.completeRegistration")}
        </Button>

        {onCancel && (
          <Button
            variant="outlined"
            fullWidth
            className="notranslate"
            translate="no"
            onClick={onCancel}
            disabled={isLoading}
            sx={{ mt: 1 }}
          >
            {myTranslate("common.cancel")}
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default GoogleRegistrationForm;
